import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  User,
  createUserWithEmailAndPassword as createUserWeb,
  getAdditionalUserInfo,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInAnonymously as signInAnonymouslyFirebase,
  signInWithEmailAndPassword as signInWeb,
  signInWithCredential,
  signInWithPopup,
  signOut as signOutWeb,
  updateProfile,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ObsessLessUser } from "../../types/obsessless-user";
import { firebaseAuth, firestore } from "../config/firebase";

export enum AuthStatus {
  Initializing = "initializing",
  Unauthenticated = "unauthenticated",
  AuthenticatedLoading = "authenticated-loading",
  AuthenticatedReady = "authenticated-ready",
}

type AuthContextType = {
  user: User | null;
  readyUser: ObsessLessUser | null;
  authStatus: AuthStatus;
  isAnonymous: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInAnonymously: () => Promise<void>;
  linkAccountWithEmail: (
    displayName: string,
    email: string,
    password: string,
  ) => Promise<void>;
  linkAccountWithGoogle: () => Promise<void>;
  linkAccountWithApple: () => Promise<void>;
  handleSignOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  reauthenticateWithEmail: (password: string) => Promise<void>;
  reauthenticateWithGoogle: () => Promise<void>;
  reauthenticateWithApple: () => Promise<void>;
  refreshAuthToken: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

// Utility function to check subscription status from Firebase token
const checkSubscriptionStatus = async (user: User | null): Promise<boolean> => {
  if (!user) return false;

  try {
    const tokenResult = await user.getIdTokenResult();
    const revenueCatEntitlements = tokenResult.claims.revenueCatEntitlements;

    return Array.isArray(revenueCatEntitlements) && revenueCatEntitlements.includes('Pro');
  } catch (error) {
    console.error('Error checking subscription status from token:', error);
    return false;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Initializing);
  const [isProUser, setIsProUser] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isClaimsLoaded, setIsClaimsLoaded] = useState(false);
  type FirestoreUserFields = Omit<ObsessLessUser, "uid" | "isProUser">;
  const [firestoreUserFields, setFirestoreUserFields] = useState<FirestoreUserFields | null>(null);

  // Check subscription claims whenever user changes
  useEffect(() => {
    if (!user) {
      setIsProUser(false);
      setIsClaimsLoaded(false);
      return;
    }
    const updateSubscriptionStatus = async () => {
      const proStatus = await checkSubscriptionStatus(user);
      setIsProUser(proStatus);
      setIsClaimsLoaded(true);
    };
    updateSubscriptionStatus();
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (usr) => {
      setUser(usr);
      if (!usr) {
        setAuthStatus(AuthStatus.Unauthenticated);
        setIsProfileLoaded(false);
        setIsClaimsLoaded(false);
        setFirestoreUserFields(null);
      } else {
        setAuthStatus(AuthStatus.AuthenticatedLoading);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const userRef = doc(firestore, `user_info/${user.uid}`);
      const unsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const fields: FirestoreUserFields = {
            email: data.email ?? null,
            displayName: data.displayName ?? null,
            createdAt: data.createdAt?.toDate?.() ?? new Date(0),
            freeChatUsage: data.freeChatUsage,
            hasFinishedOnboarding: Boolean(data.hasFinishedOnboarding),
            onboardingProgress: data.onboardingProgress ?? undefined,
            referralCode: data.referralCode ?? undefined,
            questJourney: data.questJourney ?? undefined,
          };
          setFirestoreUserFields(fields);
          setIsProfileLoaded(true);
        } else {
          setFirestoreUserFields(null);
          setIsProfileLoaded(true);
        }
      });
      return () => unsubscribe();
    } else {
      setFirestoreUserFields(null);
      setIsProfileLoaded(false);
    }
  }, [user, user?.isAnonymous]);

  // Compose a full ObsessLessUser when all pieces are present
  const readyUser = useMemo<ObsessLessUser | null>(() => {
    if (!user || !firestoreUserFields) return null;
    return {
      uid: user.uid,
      isProUser,
      ...firestoreUserFields,
    };
  }, [user, firestoreUserFields, isProUser]);

  // Check if the current user is anonymous
  const isAnonymous = useMemo(() => {
    return user?.isAnonymous ?? false;
  }, [user]);

  // Flip to ready once profile and claims are loaded and we can build a readyUser
  useEffect(() => {
    if (authStatus === "authenticated-loading" && isProfileLoaded && isClaimsLoaded && readyUser) {
      setAuthStatus(AuthStatus.AuthenticatedReady);
    }
  }, [authStatus, isProfileLoaded, isClaimsLoaded, readyUser]);

  const signIn = async (email: string, password: string) => {
    if (Capacitor.isNativePlatform()) {
      const nativeResult =
        await FirebaseAuthentication.signInWithEmailAndPassword({
          email,
          password,
        });

      if (!nativeResult.user) throw new Error("No native user returned");
    }

    await signInWeb(firebaseAuth, email, password);
  };

  const signInAnonymously = async () => {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signInAnonymously();
    }
    await signInAnonymouslyFirebase(firebaseAuth);
  };

  const linkAccountWithEmail = async (
    displayName: string,
    email: string,
    password: string,
  ) => {
    if (!user) throw new Error("User not found");
    if (!user.isAnonymous) throw new Error("User is not anonymous");
    if (!displayName || !email || !password)
      throw new Error("All fields are required");

    const credential = EmailAuthProvider.credential(email, password);
    await updateProfile(user, { displayName });
    await linkWithCredential(user, credential);
  };

  const linkAccountWithGoogle = async () => {
    if (!user) throw new Error("User not found");
    if (!user.isAnonymous) throw new Error("User is not anonymous");

    if (Capacitor.isNativePlatform()) {
      const googleResult = await FirebaseAuthentication.signInWithGoogle();
      if (!googleResult.credential?.idToken) {
        throw new Error("No ID token returned from Google Sign-In");
      }
      const credential = GoogleAuthProvider.credential(
        googleResult.credential.idToken,
        googleResult.credential.accessToken,
      );
      await linkWithCredential(user, credential);
    } else {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      await linkWithPopup(user, provider);
    }
  };

  const linkAccountWithApple = async () => {
    if (!user) throw new Error("User not found");
    if (!user.isAnonymous) throw new Error("User is not anonymous");

    if (Capacitor.isNativePlatform()) {
      const appleResult = await FirebaseAuthentication.signInWithApple({
        skipNativeAuth: true,
      });
      if (!appleResult.credential?.idToken) {
        throw new Error("No ID token returned from Apple Sign-In");
      }
      const provider = new OAuthProvider("apple.com");
      const credential = provider.credential({
        idToken: appleResult.credential.idToken,
        rawNonce: appleResult.credential.nonce,
      });
      await linkWithCredential(user, credential);
    } else {
      const provider = new OAuthProvider("apple.com");
      provider.addScope('email');
      provider.addScope('name');
      await linkWithPopup(user, provider);
    }
  };

  const handleSignOut = async () => {
    setAuthStatus(AuthStatus.Initializing);
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signOut();
    }
    await signOutWeb(firebaseAuth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(firebaseAuth, email);
  };

  const signInWithApple = async () => {
    let userCredential;
    if (Capacitor.isNativePlatform()) {
      const appleResult = await FirebaseAuthentication.signInWithApple({
        skipNativeAuth: true,
      });

      if (!appleResult.credential?.idToken) {
        throw new Error("No ID token returned from Apple Sign-In");
      }

      const provider = new OAuthProvider("apple.com");
      const credential = provider.credential({
        idToken: appleResult.credential.idToken,
        rawNonce: appleResult.credential.nonce,
      });
      userCredential = await signInWithCredential(firebaseAuth, credential);
    } else {
      const provider = new OAuthProvider("apple.com");
      provider.addScope('email');
      provider.addScope('name');
      userCredential = await signInWithPopup(firebaseAuth, provider);
    }

    // Track registration if this is a new user
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
    if (additionalUserInfo?.isNewUser) {
    }
  };

  const signInWithGoogle = async () => {
    let userCredential;
    if (Capacitor.isNativePlatform()) {
      const googleResult = await FirebaseAuthentication.signInWithGoogle();

      if (!googleResult.credential?.idToken) {
        throw new Error("No ID token returned from Google Sign-In");
      }
      const credential = GoogleAuthProvider.credential(
        googleResult.credential.idToken,
        googleResult.credential.accessToken,
      );
      userCredential = await signInWithCredential(firebaseAuth, credential);
    } else {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      userCredential = await signInWithPopup(firebaseAuth, provider);
    }

    // Track registration if this is a new user
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
  };

  const deleteAccount = async () => {
    if (!user) throw new Error("User not found");
    try {
      setAuthStatus(AuthStatus.AuthenticatedLoading);
      await user.delete();
    } catch (error: any) {
      console.error("Error deleting account:", error);
      throw error;
    }
  };

  const reauthenticateWithEmail = async (password: string) => {
    if (!user || !user.email) throw new Error("User not found");
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    } catch (error: any) {
      console.error("Error reauthenticating with email:", error);
      throw error;
    }
  };

  const reauthenticateWithGoogle = async () => {
    if (!user) throw new Error("User not found");
    try {
      if (Capacitor.isNativePlatform()) {
        const googleResult = await FirebaseAuthentication.signInWithGoogle();
        if (!googleResult.credential?.idToken) {
          throw new Error("No ID token returned from Google Sign-In");
        }
        const credential = GoogleAuthProvider.credential(
          googleResult.credential.idToken,
          googleResult.credential.accessToken,
        );
        await reauthenticateWithCredential(user, credential);
      } else {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: 'select_account'
        });
        const webCredential = await signInWithPopup(firebaseAuth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(webCredential);
        if (credential) {
          await reauthenticateWithCredential(user, credential);
        }
      }
    } catch (error: any) {
      console.error("Error reauthenticating with Google:", error);
      throw error;
    }
  };

  const reauthenticateWithApple = async () => {
    if (!user) throw new Error("User not found");
    try {
      if (Capacitor.isNativePlatform()) {
        const appleResult = await FirebaseAuthentication.signInWithApple({
          skipNativeAuth: true
        });
        if (!appleResult.credential?.idToken) {
          throw new Error("No ID token returned from Apple Sign-In");
        }
        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({
          idToken: appleResult.credential.idToken,
          rawNonce: appleResult.credential.nonce,
        });
        await reauthenticateWithCredential(user, credential);
      } else {
        const provider = new OAuthProvider("apple.com");
        provider.addScope('email');
        provider.addScope('name');
        const webCredential = await signInWithPopup(firebaseAuth, provider);
        const credential = OAuthProvider.credentialFromResult(webCredential);
        if (credential) {
          await reauthenticateWithCredential(user, credential);
        }
      }
    } catch (error: any) {
      console.error("Error reauthenticating with Apple:", error);
      throw error;
    }
  };

  const refreshAuthToken = async (): Promise<boolean> => {
    if (!user) throw new Error("User not found");
    try {
      // Force refresh the Firebase Auth token to get updated custom claims (entitlements)
      await user.getIdToken(true);

      // Update subscription status after token refresh
      const proStatus = await checkSubscriptionStatus(user);
      setIsProUser(proStatus);
      setIsClaimsLoaded(true);
      return proStatus;
    } catch (error: any) {
      console.error("Error refreshing auth token:", error);
      throw error;
    }
  };

  const value = {
    user,
    readyUser,
    authStatus,
    isAnonymous,
    signIn,
    signInAnonymously,
    linkAccountWithEmail,
    linkAccountWithGoogle,
    linkAccountWithApple,
    handleSignOut,
    resetPassword,
    signInWithApple,
    signInWithGoogle,
    deleteAccount,
    reauthenticateWithEmail,
    reauthenticateWithGoogle,
    reauthenticateWithApple,
    refreshAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
